import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Box from '@mui/material/Box';
import { useAppSelector } from '../../redux/hooks';
import { selectDirection } from '../../redux/selectors';
import type { Place } from '../../types/place';

import { addDefaultSource } from '../../services/errorHandlers';
import ExpandableParagraph from '../common/ExpandableParagraph';
import { createImageUrl } from '../../services/data';
import { Typography } from '@mui/material';

const [lineHeight, gap] = [2.3, 18]

interface StationProps {
  place: Place;
}

const Station: React.FC<StationProps> = ({ place }) => {
    const [lineClamp, setLineClamp] = useState<number | null>(null)
    const direction = useAppSelector(selectDirection)
    const nameRef = useRef<HTMLElement>(null);
    const imgRef = useRef<HTMLImageElement>(null);
    const navigate = useNavigate()
    const {slug} = useParams()

    useEffect(() => {
        if (imgRef.current && nameRef.current) {
            const imgHeight = imgRef.current.offsetHeight;
            const nameHeight = nameRef.current.offsetHeight;
            const calculatedHeight = imgHeight - nameHeight - gap;
            setLineClamp(Math.ceil(calculatedHeight / (lineHeight * 10)));
        }
    }, [place])

    const handleClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        navigate(`/${slug}/${place.id}`)
    }

    return (
        <Box className='thumbnail' 
            onClick={handleClick}
            sx={{display: 'flex', 
            gap: '1.5rem', 
            direction: direction, 
            width: '100%', 
            cursor: 'pointer'
            }}>
            <div>
            <Box sx={{width: '12rem', height: '12rem', overflow: 'hidden', position: 'relative', borderRadius: '0.7rem'}}>
                    <img style={{objectFit: 'contain'}} src={createImageUrl(place.information_photo)} loading='lazy' alt={`thumbnail-${place.id}`} onError={addDefaultSource} ref={imgRef}/>
                </Box>
            </div>

            <Box sx={{display: 'flex', flexDirection: 'column', gap: '1.8', textAlign: 'start', justifyContent: 'center'}}>
                <Typography variant='body1' className='station-name' ref={nameRef} 
                    sx={{
                        fontWeight: '900',
                        lineHeight: '2.3rem',
                        margin: '0',
                        letterSpacing: '-0.32px', fontFamily: 'SimplerPro'
                    }}
                >
                    {place.translations[0].information_title}
                </Typography>
                {
                    lineClamp && place.translations[0].information_short_description && 
                    <ExpandableParagraph content={place.translations[0].information_short_description} lineClamp={lineClamp} />
                }
            </Box>
        </Box>
    );
};

export default Station;
